import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabaseClient';
import SnackbarMessage from './SnackbarMessage';

const SUBSTITUTE_REASONS = [
  'Sick Leave',
  'Personal Leave',
  'Vacation',
  'Emergency',
  'Family Emergency',
  'Other',
];

function CoverageRequestForm({ open, onClose, onSuccess, existingRequest = null }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const [formData, setFormData] = useState({
    class_id: '',
    start_date: null,
    end_date: null,
    reason: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  const fetchTeacherClasses = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('id, name, shift')
        .eq('teacher_id', user.id)
        .order('name');

      if (error) throw error;
      setClasses(data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load your classes',
        severity: 'error'
      });
    }
  }, [user.id, setSnackbar]);

  const resetForm = useCallback(() => {
    setFormData({
      class_id: '',
      start_date: null,
      end_date: null,
      reason: '',
      notes: '',
    });
    setErrors({});
  }, []);

  useEffect(() => {
    if (open) {
      fetchTeacherClasses();
      if (existingRequest) {
        setFormData({
          class_id: existingRequest.class_id,
          start_date: dayjs(existingRequest.start_date),
          end_date: dayjs(existingRequest.end_date),
          reason: existingRequest.reason,
          notes: existingRequest.description || '',
        });
      } else {
        resetForm();
      }
    }
  }, [open, existingRequest, fetchTeacherClasses, resetForm]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.class_id) {
      newErrors.class_id = 'Please select a class';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      if (formData.start_date.isAfter(formData.end_date)) {
        newErrors.end_date = 'End date must be after start date';
      }
      
      if (formData.start_date.isBefore(dayjs(), 'day')) {
        newErrors.start_date = 'Start date cannot be in the past';
      }
    }

    if (!formData.reason) {
      newErrors.reason = 'Please select a reason for coverage';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const requestData = {
        teacher_id: user.id,
        class_id: formData.class_id,
        start_date: formData.start_date.format('YYYY-MM-DD'),
        end_date: formData.end_date.format('YYYY-MM-DD'),
        reason: formData.reason,
        description: formData.notes.trim() || null,
        status: 'pending',
      };

      let result;
      if (existingRequest) {
        result = await supabase
          .from('coverage_requests')
          .update(requestData)
          .eq('id', existingRequest.id)
          .select();
      } else {
        result = await supabase
          .from('coverage_requests')
          .insert([requestData])
          .select();
      }

      if (result.error) throw result.error;

      setSnackbar({
        open: true,
        message: existingRequest 
          ? 'Coverage request updated successfully' 
          : 'Coverage request submitted successfully',
        severity: 'success'
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting coverage request:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit coverage request',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const getDaysDifference = () => {
    if (formData.start_date && formData.end_date) {
      return formData.end_date.diff(formData.start_date, 'day') + 1;
    }
    return 0;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {existingRequest ? 'Edit Coverage Request' : 'Request Coverage'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {classes.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You don't have any classes assigned. Please contact an administrator.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.class_id}>
                  <InputLabel>Class *</InputLabel>
                  <Select
                    value={formData.class_id}
                    label="Class *"
                    onChange={(e) => handleInputChange('class_id', e.target.value)}
                    disabled={loading || classes.length === 0}
                  >
                    {classes.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {cls.name} {cls.shift && `(${cls.shift})`}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.class_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.class_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date *"
                  value={formData.start_date}
                  onChange={(value) => handleInputChange('start_date', value)}
                  disabled={loading}
                  minDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.start_date,
                      helperText: errors.start_date,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date *"
                  value={formData.end_date}
                  onChange={(value) => handleInputChange('end_date', value)}
                  disabled={loading}
                  minDate={formData.start_date || dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.end_date,
                      helperText: errors.end_date,
                    },
                  }}
                />
              </Grid>

              {formData.start_date && formData.end_date && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Duration: {getDaysDifference()} day{getDaysDifference() !== 1 ? 's' : ''}
                  </Typography>
                </Grid>
              )}


              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.reason}>
                  <InputLabel>Reason *</InputLabel>
                  <Select
                    value={formData.reason}
                    label="Reason *"
                    onChange={(e) => handleInputChange('reason', e.target.value)}
                    disabled={loading}
                  >
                    {SUBSTITUTE_REASONS.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.reason && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.reason}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  helperText="Any additional information for the substitute teacher"
                  disabled={loading}
                  multiline
                  rows={3}
                  placeholder="Any additional notes or instructions for the substitute teacher..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || classes.length === 0}
          >
            {loading ? 'Submitting...' : existingRequest ? 'Update Request' : 'Submit Request'}
          </Button>
        </DialogActions>

        <SnackbarMessage
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      </Dialog>
    </LocalizationProvider>
  );
}

export default CoverageRequestForm;
